import { useQuery, type QueryClient } from '@tanstack/react-query';

import { adminGetCollection, getAdminGetCollectionQueryKey } from '@r/api/generated/client.ts';
import type {
  AdminGetCollectionParams,
  ProductCategoryProductCategoryReadModel,
  ProductCategoryProductCategoryWriteJsonMergePatchModel
} from '@r/api/generated/client.schemas.ts';

const ADMIN_SHOP_CATEGORIES_MAX_PER_PAGE = 30;

export type AdminShopCategoriesCollectionParams = Pick<AdminGetCollectionParams, 'label'>;

/** Clé React Query stable pour les catégories d'une boutique. */
export function getAdminShopCategoriesQueryKey(
  shopId: string | number,
  params?: AdminGetCollectionParams
) {
  return getAdminGetCollectionQueryKey(String(shopId), params);
}

/** Clé React Query pour la collection complète (toutes les pages agrégées). */
export function getAdminShopCategoriesAllPagesQueryKey(
  shopId: string | number,
  params?: AdminShopCategoriesCollectionParams
) {
  return [
    ...getAdminGetCollectionQueryKey(String(shopId)),
    'all-pages',
    ...(params ? [params] : [])
  ] as const;
}

/** Charge toutes les catégories admin en parcourant la pagination API. */
export async function fetchAllAdminShopCategories(
  shopId: string | number,
  params?: AdminShopCategoriesCollectionParams,
  signal?: AbortSignal
): Promise<ProductCategoryProductCategoryReadModel[]> {
  let page = 1;
  let lastPage = 1;
  const categories: ProductCategoryProductCategoryReadModel[] = [];

  do {
    const response = await adminGetCollection(
      String(shopId),
      { ...params, page, perPage: ADMIN_SHOP_CATEGORIES_MAX_PER_PAGE },
      undefined,
      signal
    );
    categories.push(...(response.data ?? []));
    lastPage = response.pagination?.last ?? 1;
    page += 1;
  } while (page <= lastPage);

  return categories;
}

/** Hook React Query qui agrège toutes les pages de catégories pour le canvas React Flow. */
export function useAdminShopCategoriesCollection(
  shopId: number,
  params?: AdminShopCategoriesCollectionParams
) {
  return useQuery({
    queryKey: getAdminShopCategoriesAllPagesQueryKey(shopId, params),
    queryFn: ({ signal }) => fetchAllAdminShopCategories(shopId, params, signal),
    placeholderData: (previousData) => previousData
  });
}

/** Invalide la liste des catégories après création, édition ou changement de parent. */
export function invalidateAdminShopCategoriesQueries(
  queryClient: QueryClient,
  shopId: string | number
) {
  queryClient.invalidateQueries({ queryKey: getAdminShopCategoriesQueryKey(shopId) });
}

/** IRI ProductCategory attendue dans les payloads PATCH (ex. parent). */
export function getProductCategoryIri(categoryId: number): string {
  return `/product_categories/${categoryId}`;
}

/** Extrait l'id d'une IRI `/product_categories/{id}`. */
export function parseProductCategoryIdFromIri(value: string): number | null {
  const match = value.match(/\/product_categories\/(\d+)\/?$/);
  return match ? Number(match[1]) : null;
}

type CategoryParentRef = ProductCategoryProductCategoryReadModel | string | null | undefined;

type CategoryChildRef = ProductCategoryProductCategoryReadModel | string;

/** Résout l'id parent depuis un objet ou une IRI string renvoyée par l'API. */
export function getCategoryParentId(category: { parent?: CategoryParentRef }): number | null {
  const { parent } = category;
  if (!parent) return null;
  if (typeof parent === 'string') return parseProductCategoryIdFromIri(parent);
  return parent.id ?? null;
}

/** Indique si une entrée enfant est un objet catégorie (et non une IRI brute). */
export function isCategoryChildEntry(
  child: CategoryChildRef
): child is ProductCategoryProductCategoryReadModel {
  return typeof child === 'object' && child !== null && 'id' in child;
}

/** Retourne true si la catégorie possède au moins une sous-catégorie directe. */
export function categoryHasChildren(category: ProductCategoryProductCategoryReadModel): boolean {
  return (category.childs ?? []).length > 0;
}

/** Normalise la référence parent (objet ou IRI) en objet minimal { id, label }. */
function normalizeCategoryParent(
  category: ProductCategoryProductCategoryReadModel,
  inferredParentId: number | null
): ProductCategoryProductCategoryReadModel['parent'] {
  const parentId = getCategoryParentId(category) ?? inferredParentId;
  return parentId !== null ? { id: parentId, label: '' } : undefined;
}

/** Aplatit l'arbre API en normalisant parent/childs (objets ou IRI). */
export function flattenCategories(
  categories: readonly ProductCategoryProductCategoryReadModel[]
): ProductCategoryProductCategoryReadModel[] {
  const flattened: ProductCategoryProductCategoryReadModel[] = [];
  const seen = new Set<number>();

  const visit = (
    category: ProductCategoryProductCategoryReadModel,
    inferredParentId: number | null = null
  ) => {
    if (seen.has(category.id)) return;
    seen.add(category.id);

    flattened.push({
      ...category,
      parent: normalizeCategoryParent(category, inferredParentId),
      childs: (category.childs ?? []).filter(isCategoryChildEntry)
    });

    for (const child of category.childs ?? []) {
      if (!isCategoryChildEntry(child)) continue;
      visit(child, category.id);
    }
  };

  for (const category of categories) {
    visit(category);
  }

  return flattened;
}

/** Indexe les catégories aplaties par identifiant. */
export function buildCategoryLookup(
  categories: readonly ProductCategoryProductCategoryReadModel[]
): Map<number, ProductCategoryProductCategoryReadModel> {
  return new Map(flattenCategories(categories).map((category) => [category.id, category]));
}

/** Ne conserve que les catégories sans enfants, assignables directement à un produit. */
export function getAssignableProductCategories(
  categories: readonly ProductCategoryProductCategoryReadModel[]
): ProductCategoryProductCategoryReadModel[] {
  const flatCategories = flattenCategories(categories);
  const parentIds = new Set(
    flatCategories
      .map((category) => getCategoryParentId(category))
      .filter((parentId): parentId is number => parentId !== null)
  );

  return flatCategories.filter(
    (category) => !categoryHasChildren(category) && !parentIds.has(category.id)
  );
}

/** Payload PATCH pour rattacher une sous-catégorie à sa principale. */
export function buildAttachParentPatchPayload(
  parentCategoryId: number
): ProductCategoryProductCategoryWriteJsonMergePatchModel {
  return {
    parent: getProductCategoryIri(
      parentCategoryId
    ) as unknown as ProductCategoryProductCategoryWriteJsonMergePatchModel['parent']
  };
}
